/*
 *
 *  * (C) Copyright 2006-2020 hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * All Rights Reserved.
 *  *
 *  * These materials are unpublished, proprietary, confidential source code of
 *  * hSenid Mobile Solutions (Pvt) Limited and constitute a TRADE SECRET
 *  * of hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * hSenid Mobile Solutions (Pvt) Limited retains all title to and intellectual
 *  * property rights in these materials.
 *  *
 *
 */

import produce from "immer";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import {
  LoadingStatus,
  usePortalStateSelector,
} from "../../../rootReducer/actions";
import {
  getAppDetails,
  getReviews,
  App,
  AppReview,
  submitReview,
  fromDeveloper,
  getUserRating,
  AppRating,
  addAppRating,
  getSubsptionStatus,
  getBuildfile,
  BuildfileInfo,
  AppDetailsResp,
  SearchResp,
  AppReviewResp,
  UserRatingResponse,
  SubscriptionResponse,
} from "../../../api/apiCalls";
import { MainPageEvent } from "../../MainUi";
// import { redirectToHome } from "../../MainUi/MainUi";

export enum AppDetailsPageEvent {
  RESET = "RESET",
  GET_APP_DETAILS_PAGE_DATA = "GET_APP_DETAILS_PAGE_DATA",
  GET_APP_DETAILS_PAGE_DATA_RESULT = "GET_APP_DETAILS_PAGE_DATA_RESULT",
  GET_APP_DETAILS_PAGE_DATA_FAILURE = "GET_APP_DETAILS_PAGE_DATA_FAILURE",
  RESET_APP_DETAILS_PAGE_DATA = "RESET_APP_DETAILS_PAGE_DATA",
  SUBMIT_REVIEW = "SUBMIT_REVIEW",
  SUBMIT_REVIEW_RESULT = "SUBMIT_REVIEW_RESULT",
  SHOW_ADD_REVIEW_MODAL = "SHOW_ADD_REVIEW_MODAL",
  HIDE_ADD_REVIEW_MODAL = "HIDE_ADD_REVIEW_MODAL",
  SET_USER_RATING = "SET_USER_RATING",
  SUBMIT_APP_RATING = "SUBMIT_APP_RATING",
  SUBMIT_APP_RATING_SUCCESS = "SUBMIT_APP_RATING_SUCCESS",
  SUBMIT_APP_RATING_FAILURE = "SUBMIT_APP_RATING_FAILURE",
  DOWNLOAD_BUILDFILE = "DOWNLOAD_BUILDFILE",
  DOWNLOAD_BUILDFILE_SUCCESS = "DOWNLOAD_BUILDFILE_SUCCESS",
  DOWNLOAD_BUILDFILE_FAILURE = "DOWNLOAD_BUILDFILE_FAILURE",
  DOWNLOAD_BUILDFILE_JWT_EXPIRED = "DOWNLOAD_BUILDFILE_JWT_EXPIRED",
  SHOW_BUILDFILE_ERROR_MODAL = "SHOW_BUILDFILE_ERROR_MODAL",
  HIDE_BUILDFILE_ERROR_MODAL = "HIDE_BUILDFILE_ERROR_MODAL",
}

export interface AppDetailsPageResults {
  appDetails: App;
  suggestedApps: App[];
  reviews: AppReview[];
  userRating: number;
  subscriptionStatus: boolean;
}

export interface GetAppDetailsPageAction {
  type: AppDetailsPageEvent.GET_APP_DETAILS_PAGE_DATA;
  appId: string;
  msisdn: string;
}

export interface GetAppDetailsPageResultsAction {
  type: AppDetailsPageEvent.GET_APP_DETAILS_PAGE_DATA_RESULT;
  results: AppDetailsPageResults;
}

export interface GetAppDetailsPageFailureAction {
  type: AppDetailsPageEvent.GET_APP_DETAILS_PAGE_DATA_FAILURE;
  results: string;
}
export interface ResetAppDetailsPageFailureAction {
  type: AppDetailsPageEvent.RESET_APP_DETAILS_PAGE_DATA;
}

export interface SubmitReviewAction {
  type: AppDetailsPageEvent.SUBMIT_REVIEW;
  payload: AppReview;
  token: string;
}

export interface SubmitReviewResultAction {
  type: AppDetailsPageEvent.SUBMIT_REVIEW_RESULT;
}

export interface ShowAddReviewModalAction {
  type: AppDetailsPageEvent.SHOW_ADD_REVIEW_MODAL;
}

export interface HideAddReviewModalAction {
  type: AppDetailsPageEvent.HIDE_ADD_REVIEW_MODAL;
}

export interface SetUserRatingAction {
  type: AppDetailsPageEvent.SET_USER_RATING;
  payload: number;
}

export interface SubmitAppRatingAction {
  type: AppDetailsPageEvent.SUBMIT_APP_RATING;
  payload: AppRating;
  token: string;
}

export interface SubmitAppRatingSuccessAction {
  type: AppDetailsPageEvent.SUBMIT_APP_RATING_SUCCESS;
  app: App;
}

export interface SubmitAppRatingFailureAction {
  type: AppDetailsPageEvent.SUBMIT_APP_RATING_FAILURE;
}

export interface DownloadBuildfileAction {
  type: AppDetailsPageEvent.DOWNLOAD_BUILDFILE;
  payload: BuildfileInfo;
}

export interface DownloadBuildfileSuccessAction {
  type: AppDetailsPageEvent.DOWNLOAD_BUILDFILE_SUCCESS;
}

export interface DownloadBuildfileFailureAction {
  type: AppDetailsPageEvent.DOWNLOAD_BUILDFILE_FAILURE;
}

export interface DownloadBuildfileFailureJwtExpiredAction {
  type: AppDetailsPageEvent.DOWNLOAD_BUILDFILE_JWT_EXPIRED;
}

export interface ShowBuildfileErrorModalAction {
  type: AppDetailsPageEvent.SHOW_BUILDFILE_ERROR_MODAL;
}

export interface HideBuildfileErrorModalAction {
  type: AppDetailsPageEvent.HIDE_BUILDFILE_ERROR_MODAL;
}

export type AppDetailsPageAction =
  | GetAppDetailsPageAction
  | GetAppDetailsPageResultsAction
  | GetAppDetailsPageFailureAction
  | ResetAppDetailsPageFailureAction
  | SubmitReviewAction
  | SubmitReviewResultAction
  | ShowAddReviewModalAction
  | HideAddReviewModalAction
  | SetUserRatingAction
  | SubmitAppRatingAction
  | SubmitAppRatingSuccessAction
  | SubmitAppRatingFailureAction
  | ShowBuildfileErrorModalAction
  | HideBuildfileErrorModalAction
  | DownloadBuildfileAction
  | DownloadBuildfileSuccessAction
  | DownloadBuildfileFailureAction
  | DownloadBuildfileFailureJwtExpiredAction;

export interface AppDetailsPageData {
  loadingStatus: LoadingStatus;
  authState: boolean;
  msisdn?: string;
  otp?: string;
  appDetails?: App;
  userRating: number;
  subscriptionStatus: boolean;
  suggestedApps?: App[];
  reviews?: AppReview[];
  addingReview: boolean;
  showAddReviewModal: boolean;
  showBuildfileErrorModal: boolean;
  downloadingApp?: BuildfileInfo;
  downloading: boolean;
}

const initialState = {
  loadingStatus: LoadingStatus.LOADING_STARTED,
  authState: false,
  notification: {
    show: false,
  },
  addingReview: false,
  showAddReviewModal: false,
  userRating: 0,
  subscriptionStatus: false,
  showBuildfileErrorModal: false,
  downloading: false,
};

/**
 * Reducer
 */
export function appDetailsPageReducer(
  state: AppDetailsPageData = initialState,
  action: AppDetailsPageAction
) {
  return produce(state, (draft) => {
    switch (action.type) {
      case AppDetailsPageEvent.GET_APP_DETAILS_PAGE_DATA_RESULT: {
        draft.loadingStatus = LoadingStatus.LOADING_SUCCESS;
        draft.appDetails = action.results.appDetails;
        draft.userRating = action.results.userRating;
        draft.suggestedApps = action.results.suggestedApps;
        draft.reviews = action.results.reviews;
        draft.subscriptionStatus = action.results.subscriptionStatus;
        break;
      }
      case AppDetailsPageEvent.GET_APP_DETAILS_PAGE_DATA_FAILURE: {
        draft.loadingStatus = LoadingStatus.LOADING_ERROR;
        break;
      }
      case AppDetailsPageEvent.RESET_APP_DETAILS_PAGE_DATA: {
        draft.loadingStatus = LoadingStatus.LOADING_STARTED;
        draft.appDetails = undefined;
        draft.suggestedApps = undefined;
        draft.reviews = undefined;
        break;
      }

      case AppDetailsPageEvent.SUBMIT_REVIEW: {
        draft.addingReview = true;
        break;
      }
      case AppDetailsPageEvent.SUBMIT_REVIEW_RESULT: {
        draft.addingReview = false;
        draft.showAddReviewModal = false;
        break;
      }
      case AppDetailsPageEvent.SHOW_ADD_REVIEW_MODAL: {
        draft.showAddReviewModal = true;
        break;
      }
      case AppDetailsPageEvent.HIDE_ADD_REVIEW_MODAL: {
        draft.showAddReviewModal = false;
        break;
      }
      case AppDetailsPageEvent.SET_USER_RATING: {
        draft.userRating = action.payload;
        break;
      }
      case AppDetailsPageEvent.SUBMIT_APP_RATING_SUCCESS: {
        draft.appDetails = action.app;
        break;
      }
      case AppDetailsPageEvent.SHOW_BUILDFILE_ERROR_MODAL: {
        draft.showBuildfileErrorModal = true;
        break;
      }
      case AppDetailsPageEvent.HIDE_BUILDFILE_ERROR_MODAL: {
        draft.showBuildfileErrorModal = false;
        break;
      }
      case AppDetailsPageEvent.DOWNLOAD_BUILDFILE: {
        draft.downloading = true;
        break;
      }
      case AppDetailsPageEvent.DOWNLOAD_BUILDFILE_SUCCESS: {
        draft.downloading = false;
        break;
      }
      case AppDetailsPageEvent.DOWNLOAD_BUILDFILE_FAILURE: {
        draft.downloading = false;
        draft.showBuildfileErrorModal = true;
        break;
      }
      case AppDetailsPageEvent.DOWNLOAD_BUILDFILE_JWT_EXPIRED: {
        draft.downloading = false;
        break;
      }
    }
  });
}

/**
 * Selector
 */
export function useAppDetailsPageDataSelector(): AppDetailsPageData {
  return usePortalStateSelector<AppDetailsPageData>(
    (ev) => ev.appDetailsPageData
  );
}

/**
 * Page Saga
 */
export const appDetailsPageSagas = [
  getAppDetailsPageDataSaga,
  submitReviewSaga,
  addAppRatingSaga,
  downloadBuildfileSaga,
];

/**
 * Watcher Sagas
 */
function* getAppDetailsPageDataSaga() {
  yield takeLatest(
    AppDetailsPageEvent.GET_APP_DETAILS_PAGE_DATA,
    callGetAppDetailsPageData
  );
}

function* submitReviewSaga() {
  yield takeLatest(AppDetailsPageEvent.SUBMIT_REVIEW, callSubmitReview);
}

function* addAppRatingSaga() {
  yield takeLatest(AppDetailsPageEvent.SUBMIT_APP_RATING, callSubmitRating);
}

function* downloadBuildfileSaga() {
  yield takeLatest(
    AppDetailsPageEvent.DOWNLOAD_BUILDFILE,
    callDownloadBuildfile
  );
}

/**
 * Handler functions
 */

const arr = ["E1008", "E1009", "E1010", "E1011", "E1012", "E1013"];

function* callGetAppDetailsPageData(action: GetAppDetailsPageAction) {
  try {
    const [
      appDetailsRes,
      suggestedAppsRes,
      reviewsRes,
      userRatingRes,
      regStatusRes,
    ]: [
      AppDetailsResp,
      SearchResp,
      AppReviewResp,
      UserRatingResponse,
      SubscriptionResponse
    ] = yield all([
      call(getAppDetails, action.appId),
      call(fromDeveloper, action.appId),
      call(getReviews, action.appId),
      sessionStorage.getItem("isAuthed") === "true" &&
        call(getUserRating, {
          appId: action.appId,
          msisdn: action.msisdn,
        }),
      sessionStorage.getItem("isAuthed") === "true" &&
        call(getSubsptionStatus, {
          appId: action.appId,
          msisdn: action.msisdn,
        }),
    ]);
    if (
      arr.includes(appDetailsRes.statusCode) ||
      arr.includes(suggestedAppsRes.statusCode) ||
      arr.includes(userRatingRes.statusCode) ||
      arr.includes(regStatusRes.statusCode)
    ) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      window.location.reload();
    } else {
      yield put({
        type: AppDetailsPageEvent.GET_APP_DETAILS_PAGE_DATA_RESULT,
        results: {
          appDetails: appDetailsRes.payload,
          suggestedApps: suggestedAppsRes.payload,
          reviews: reviewsRes.payload,
          userRating: userRatingRes.payload ? userRatingRes.payload.rating : 0,
          subscriptionStatus:
            regStatusRes?.payload?.subscriptionStatus === "REGISTERED" ||
            regStatusRes?.payload?.subscriptionStatus ===
              "INITIAL CHARGING PENDING",
        },
      });
    }
  } catch (e) {
    console.error(e);
    yield put({ type: AppDetailsPageEvent.GET_APP_DETAILS_PAGE_DATA_FAILURE });
  }
}

function* callSubmitReview(action: SubmitReviewAction) {
  try {
    const { statusCode, debugMessage, payload } = yield call(
      submitReview,
      action.payload,
      action.token
    );
    if (statusCode === "S1000") {
      yield put({
        type: AppDetailsPageEvent.SUBMIT_REVIEW_RESULT,
        results: "Successfully added review",
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "Successfully added review",
      });
    } else if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      window.location.reload();
    } else {
      yield put({
        type: AppDetailsPageEvent.SUBMIT_REVIEW_RESULT,
        results: "Error while adding review",
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "Error while adding review",
      });
    }
  } catch (e) {
    console.error(e);
    yield put({
      type: AppDetailsPageEvent.SUBMIT_REVIEW_RESULT,
      results: "Error while adding review",
    });
    yield put({
      type: MainPageEvent.SHOW_NOTIFICATION,
      payload: "Error while adding review",
    });
  }
}

function* callSubmitRating(action: SubmitAppRatingAction) {
  try {
    const { statusCode, debugMessage, payload } = yield call(
      addAppRating,
      action.payload,
      action.token
    );
    if (statusCode === "S1000") {
      yield put({
        type: AppDetailsPageEvent.SUBMIT_APP_RATING_SUCCESS,
        app: payload,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "Successfully added rating",
      });
    } else if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      window.location.reload();
    } else {
      yield put({
        type: AppDetailsPageEvent.SUBMIT_APP_RATING_FAILURE,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "Error while adding rating",
      });
    }
  } catch (e) {
    console.error(e);
    yield put({
      type: AppDetailsPageEvent.SUBMIT_APP_RATING_FAILURE,
    });
    yield put({
      type: MainPageEvent.SHOW_NOTIFICATION,
      payload: "Error while adding rating",
    });
  }
}

function* callDownloadBuildfile(action: DownloadBuildfileAction) {
  try {
    const { status, payload, fileName, statusCode, debugMessage } = yield call(
      getBuildfile,
      action.payload
    );
    console.log(status, payload, fileName, statusCode, debugMessage);

    if (status === 200) {
      const url = window.URL.createObjectURL(
        new Blob([payload], { type: "application/octet-stream" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      yield put({
        type: AppDetailsPageEvent.DOWNLOAD_BUILDFILE_SUCCESS,
      });
    } else if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      yield put({
        type: AppDetailsPageEvent.DOWNLOAD_BUILDFILE_JWT_EXPIRED,
      });
    } else {
      yield put({
        type: AppDetailsPageEvent.DOWNLOAD_BUILDFILE_FAILURE,
      });
    }
  } catch (e) {
    console.error(e);
    yield put({
      type: AppDetailsPageEvent.DOWNLOAD_BUILDFILE_FAILURE,
    });
  }
}
